import { Box } from '@mui/material';
import { FC } from 'react';
import SegmentedButtons, { popoverSectionLabelSx } from 'src/components/dispatch/SegmentedButtons';

import {
  CellColorBasis, CellColorMode, DispatchCellColorSettings,
} from './resolveCellColor';

const MODE_OPTIONS: readonly (readonly [CellColorMode, string])[] = [
  ['none', 'なし'], ['area', 'エリア'], ['prefecture', '都道府県'],
] as const;

const BASIS_OPTIONS: readonly (readonly [CellColorBasis, string])[] = [
  ['unloading', '降地基準'], ['loading', '積地基準'],
] as const;

type Props = {
  settings: DispatchCellColorSettings;
  /** 設定全体を渡す。API の PUT は置き換えなので、部分更新は作らない */
  onChange: (next: DispatchCellColorSettings) => void;
  /** 「マスタから色を変更 →」。ダイアログはこのセクションの外（ツールバー）が持つ */
  onOpenColorDialog: () => void;
};

/**
 * 表示項目メニューの中に出す「セルの色分け」（log_web_issues#338 C2）。
 * 運行バーをエリア / 都道府県で色分けする設定。
 *
 * Why not サンプルの「方面（上り / 下り）」も出す: 上り・下りの判定は首都圏を基準にした
 * 決め打ちで（route-planner regionMaster.ts:52-60）、会社ごとの拠点では合わない。
 * 出さないことをユーザーと決めた（2026-09-25）。
 *
 * Why not ダイアログもここで持つ: 表示項目の Popover の中なので、ダイアログを押すと
 * Popover 側が「外を押した」と見て閉じ、子ごとダイアログが消える。
 */
export const CellColorSection: FC<Props> = ({ settings, onChange, onOpenColorDialog }) => (
  <Box sx={{ px: 1.5, pt: 1.5, pb: 1.5 }}>
    <Box sx={{ ...popoverSectionLabelSx, mb: 0.5 }}>セルの色分け</Box>

    <SegmentedButtons
      options={MODE_OPTIONS}
      value={settings.mode}
      height={24}
      onChange={(mode) => onChange({ ...settings, mode })}
    />

    {/* 「なし」のときは基準地点が効かないので出さない（サンプル
        MatchingPresenter/index.tsx:1422 も同じ条件で囲っている）。
        見出しは付けない（あちらはモードの下にそのままボタン列を並べる。
        ラベル自体が「降地基準」で何の基準かを言っている） */}
    {settings.mode !== 'none' && (
      /* ボタン列は inline-flex。ブロックで包んで、単位の列と同じ行に並ばないようにする */
      <Box sx={{ mt: 0.75 }}>
        <SegmentedButtons
          options={BASIS_OPTIONS}
          value={settings.basis}
          height={22}
          onChange={(basis) => onChange({ ...settings, basis })}
        />
      </Box>
    )}

    {/* 色の変更は「なし」でも押せる。サンプル（同 :1447-1457）は基準地点の条件分岐の外に
        置いている。先に色を決めてから色分けを ON にできる */}
    <Box
      component="button"
      type="button"
      onClick={onOpenColorDialog}
      sx={{
        mt: 1,
        display: 'block',
        background: 'none',
        border: 'none',
        p: 0,
        fontSize: 11,
        fontWeight: 600,
        color: 'primary.main',
        cursor: 'pointer',
        textDecoration: 'underline',
      }}
    >
      マスタから色を変更 →
    </Box>
  </Box>
);
